import { useState } from "react";
import { Download, Copy } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Panel } from "@/components/Layout";

const ASSETS = {
  Logos: [
    ["postalcloud-logo.png", "Logo (PNG, dark text)"], ["postalcloud-logo.svg", "Logo (SVG, dark text)"],
    ["postalcloud-logo-white.png", "Logo (PNG, white text)"], ["postalcloud-logo-white.svg", "Logo (SVG, white text)"],
    ["postalcloud-mark.png", "App icon (PNG 512px)"], ["postalcloud-mark.svg", "App icon (SVG)"],
  ],
  Letterhead: [["postalcloud-letterhead.docx", "Letterhead (Word, editable)"], ["postalcloud-letterhead.pdf", "Letterhead (PDF, print)"]],
  "Social banners": [
    ["facebook-cover.png", "Facebook cover (1640 × 624)"], ["linkedin-banner.png", "LinkedIn banner (1584 × 396)"],
    ["x-twitter-header.png", "X / Twitter header (1500 × 500)"],
  ],
};
const PREVIEW = /\.(png|svg)$/;

const AssetCard = ([file, label]) => (
  <a key={file} href={`/brand/${file}`} download data-testid={`brand-download-${file.replace(/\./g, "-")}`}
    className="group rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden hover:border-emerald-400 transition-colors">
    <div className={`h-28 flex items-center justify-center p-3 ${file.includes("white") || file.includes("cover") || file.includes("banner") || file.includes("header") ? "bg-slate-900" : "bg-slate-50 dark:bg-slate-800"}`}>
      {PREVIEW.test(file) ? <img src={`/brand/${file}`} alt={label} className="max-h-full max-w-full object-contain" />
        : file.includes("letterhead") ? <img src="/brand/letterhead-preview.png" alt={label} className="max-h-full object-contain shadow" />
        : <span className="font-mono text-xs text-slate-400">{file.split(".").pop().toUpperCase()}</span>}
    </div>
    <div className="flex items-center justify-between gap-2 px-4 py-3 text-sm">
      <span className="font-medium">{label}</span>
      <Download size={15} className="text-slate-400 group-hover:text-emerald-600 shrink-0" />
    </div>
  </a>
);

const TEMPLATES = {
  standard: { label: "Standard signature", cta: null },
  corporate: { label: "Corporate invite", cta: ["Send verified letters across your organisation.", "Book corporate onboarding", "Corporate onboarding", "bookings@postalcloud.app"] },
  whitelabel: { label: "White-label invite", cta: ["Offer verified digital mail under your own brand.", "Become a white-label partner", "White-label partnership", "support@postalcloud.app"] },
  support: { label: "Support team", cta: ["Received a PostalCloud letter? Claim it securely with the link in your email.", "Visit postalcloud.app", null] },
};

const esc = (s) => String(s || "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

function signatureHtml(t, f, origin) {
  const { cta } = TEMPLATES[t];
  const link = cta && (cta[2] ? `mailto:${cta[3]}?subject=${encodeURIComponent(cta[2])}` : origin);
  const ctaRow = cta ? `<tr><td colspan="2" style="padding-top:12px"><table cellpadding="0" cellspacing="0" style="background:#ECFDF5;border-left:3px solid #059669;border-radius:4px"><tr><td style="padding:10px 14px;font-family:Arial,sans-serif;font-size:12px;color:#0F172A">${esc(cta[0])}<br><a href="${link}" style="color:#059669;font-weight:bold;text-decoration:none">${esc(cta[1])} &rarr;</a></td></tr></table></td></tr>` : "";
  return `<table cellpadding="0" cellspacing="0" style="font-family:Arial,sans-serif;color:#0F172A"><tr><td style="padding-right:14px;vertical-align:top"><img src="${origin}/brand/postalcloud-mark-128.png" width="48" height="48" alt="PostalCloud" style="display:block;border-radius:10px"></td><td style="border-left:2px solid #059669;padding-left:14px;vertical-align:top"><div style="font-size:15px;font-weight:bold">${esc(f.name)}</div><div style="font-size:12px;color:#64748B">${esc(f.title)}${f.title ? " &middot; " : ""}PostalCloud</div><div style="font-size:12px;margin-top:6px">${f.phone ? `${esc(f.phone)} &middot; ` : ""}<a href="mailto:${esc(f.email)}" style="color:#0F172A;text-decoration:none">${esc(f.email)}</a> &middot; <a href="${origin}" style="color:#059669;text-decoration:none">postalcloud.app</a></div><div style="font-size:11px;color:#64748B;margin-top:4px">Verified. Timestamped. Delivered.</div><div style="font-size:9px;color:#94A3B8;margin-top:6px;line-height:1.4">Pesnique Limited t/a PostalCloud &middot; Registered in England &amp; Wales, Company No. 10963497<br>4-6 Bridge Street, Annex 2 Wharfebank Mews, Tadcaster, North Yorkshire, LS24 9AL, UK</div></td></tr>${ctaRow}</table>`;
}

const SignatureBuilder = () => {
  const [t, setT] = useState("standard");
  const [f, setF] = useState({ name: "", title: "", phone: "", email: "support@postalcloud.app" });
  const origin = window.location.origin;
  const html = signatureHtml(t, f, origin);
  const copy = async () => {
    try {
      await navigator.clipboard.write([new ClipboardItem({ "text/html": new Blob([html], { type: "text/html" }), "text/plain": new Blob([`${f.name}\n${f.title} · PostalCloud\n${f.email} · postalcloud.app`], { type: "text/plain" }) })]);
      toast.success("Signature copied — paste it into your email signature settings");
    } catch {
      toast.error("Copy not supported in this browser — use Download instead");
    }
  };
  const download = () => {
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(new Blob([html], { type: "text/html" })), download: `postalcloud-signature-${t}.html` });
    a.click();
  };
  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2 space-y-4">
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(TEMPLATES).map(([k, v]) => (
            <button key={k} data-testid={`signature-type-${k}`} onClick={() => setT(k)}
              className={`px-3 py-2 rounded-lg border text-sm transition-colors ${t === k ? "border-emerald-600 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300" : "border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>{v.label}</button>
          ))}
        </div>
        {[["name", "Full name"], ["title", "Job title"], ["phone", "Phone (optional)"], ["email", "Email"]].map(([k, l]) => (
          <div key={k} className="space-y-1.5">
            <Label htmlFor={`sig-${k}`}>{l}</Label>
            <Input id={`sig-${k}`} data-testid={`signature-${k}-input`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
          </div>
        ))}
        <div className="flex gap-2">
          <button data-testid="signature-copy-btn" onClick={copy} className="flex-1 inline-flex items-center justify-center gap-2 h-10 rounded-full bg-slate-900 text-white dark:bg-white dark:text-slate-900 text-sm font-semibold hover:bg-emerald-600 transition-colors"><Copy size={15} /> Copy</button>
          <button data-testid="signature-download-btn" onClick={download} className="flex-1 inline-flex items-center justify-center gap-2 h-10 rounded-full border border-slate-300 dark:border-slate-700 text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"><Download size={15} /> Download .html</button>
        </div>
      </div>
      <div className="lg:col-span-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white p-6 overflow-x-auto" data-testid="signature-preview" dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  );
};

export const BrandKit = () => (
  <div className="space-y-8" data-testid="brand-kit">
    {Object.entries(ASSETS).map(([group, items]) => (
      <Panel key={group} title={group}>
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">{items.map(AssetCard)}</div>
      </Panel>
    ))}
    <Panel title="Email signatures">
      <p className="text-sm text-slate-500 mb-5">Pick a type, add your details, then copy and paste into Gmail, Outlook or Apple Mail signature settings.</p>
      <SignatureBuilder />
    </Panel>
  </div>
);
